import './App.scss'
import 'bootstrap-icons/font/bootstrap-icons.min.css'
import 'react-datepicker/dist/react-datepicker.css'

import React from 'react'
import { createBrowserRouter, createRoutesFromElements, Navigate, Outlet, Route, RouterProvider, useRouteError } from 'react-router-dom'
import * as Sentry from '@sentry/react'
import { AuthorizedRoute } from './components/AuthorizedRoute'
import { Permission } from './contexts/permission/PermissionModel'
import { LayoutWithLeftSidebar } from './views/LayoutWithLeftSidebar/LayoutWithLeftSidebar'
import { Upcoming } from './views/Upcoming/Upcoming'
import { Setup } from './views/Setup/Setup'
import { SchedulingPage } from './views/Scheduling/SchedulingPage'
import { SchedulingQuotaGate } from './views/Scheduling/SchedulingQuotaGate'
import { ScheduleMeetingFlow } from './views/Scheduling/flows/ScheduleMeetingFlow'
import { RescheduleMeetingFlow } from './views/Scheduling/flows/RescheduleMeetingFlow'
import { EventTypeEditorView } from './views/Setup/Scheduling/EventTypeEditor/EventTypeEditorView'
import { AccountBookingPageView } from './views/Setup/Scheduling/AccountBookingPage/AccountBookingPageView'
import { ProfileSettings } from './views/ProfileSettings/ProfileSettings'
import { ManageTeam } from './views/ManageTeam/ManageTeam'
import { Plan } from './views/Plan/Plan'
import { APIClientProvider, ClientProviderConfiguration } from './apps/professional-app/fa/APIClientProvider'
import { useAppSelector } from './store/hooks'
import { selectAuthenticatedUser, selectCrossAccountLoginLoading } from './store/auth/authSlice'
import { PastMeetings } from './apps/professional-app/fa/PastMeetings/PastMeetings'
import { DemoMeeting } from './apps/professional-app/fa/PastMeetings/DemoMeeting'
import IntegrationCallbackView from './views/Integration/IntegrationCallbackView'
import { LockScreenWrapper } from './views/Plan/LockScreenWrapper/LockScreenWrapper'
import { ManageContacts } from './views/Contacts/ManageContacts'
import { MeetingTemplateView } from './views/Setup/MeetingTemplates/MeetingTemplateView'
import { FinancialAdvisorSummary } from './apps/professional-app/fa/FinancialAdvisorSummary'
import { ImportSessionModal } from './apps/professional-app/fa/ImportSession'
import { Logout } from './views/Logout/Logout'
import { useUTMHandler } from './common/utm'
import { MFALogin } from './views/MFA/MFALogin'
import { Contact } from './views/Contacts/Contact'
import { EditAIFormView } from './views/Setup/AIForms/EditAIFormView'
import { Analytics } from './views/Analytics/Analytics'
import { InsightsView } from './views/Insights/InsightsView'
import { isInsightsEnabled } from './common/constants'
import { UserView } from './views/ManageTeam/User'
import { EmailTypeView } from './views/Setup/EmailTypes/EmailTypeView'
import { EmailHubV2 } from './views/EmailV2/EmailHubV2'
import { CleanupDatabase } from './apps/in-person-meeting/views/CleanupDatabase'
import { LiveMeetings } from './views/LiveMeetings/LiveMeetings'
import WorkflowsLanding from './views/Workflows/WorkflowsLanding'
import { CustomWorkflowsGuard } from './views/CustomWorkflows/CustomWorkflowsGuard'
import { CreateInPersonMeetingLoader } from './apps/professional-app/fa/MeetingPreparation/CreateInPersonMeetingLoader'
import { MobileSupport } from './views/InPersonMeeting/MobileSupport'
import { LoadingOverlay } from './components/CrossAccount/LoadingOverlay'
import { DeltaTrial } from './views/Plan/DeltaTrial/DeltaTrial'
import { DeltaTrialSuccess } from './views/Plan/DeltaTrial/DeltaTrialSuccess'
import { NewEmailPage } from './views/EmailV2/NewEmailPage'
import { DocumentTypeView } from './views/Setup/DocumentTypes/DocumentTypeView'
import { DocumentProspectingPage } from './views/ProcessDocument/DocumentProspectingPage'
import { OAuthAuthorize } from './views/OAuth/Authorize'
import { ROUTER_BASEPATH } from './common/constants'
import { IntegrationManagement } from './views/Integration/IntegrationManagement/IntegrationManagement'
import { AccountOpeningWizard } from './views/AccountOpeningWizard/AccountOpeningWizard'
import { AccountOpeningGate } from './views/AccountOpeningWizard/entry/AccountOpeningGate'
import { ProfileSettingsIntegrationRedirect } from './views/ProfileSettings/Integrations/ProfileSettingsIntegrationRedirect'
import { CrossClientResults } from './views/CrossClient/Results/CrossClientResults'
import { ScheduleQueryView } from './views/CrossClient/Scheduled/ScheduleQueryView'
import { EditScheduledQueryView } from './views/CrossClient/Scheduled/EditScheduledQueryView'
import { SendBookingInvitationsView } from './views/CrossClient/SendBookingInvitations/SendBookingInvitationsView'
import { FormTemplateUpdateView } from './views/Setup/FormTemplates/FormTemplateUpdateView'
import { FormTemplateCreateView } from './views/Setup/FormTemplates/FormTemplateCreateView'
import { TemplateEditor } from './views/Setup/Templates/TemplateEditor'
import { TemplateSelect } from './views/Setup/Templates/TemplateSelect'
import { GenerateTemplatePage } from './views/Setup/Templates/GenerateTemplate'
import { TemplateCreationPage } from './views/Setup/Templates/TemplateCreationPage'
import { CrossClient } from './views/CrossClient/CrossClient'
import { IntegrationSettings } from './views/Integration/IntegrationSettings'
import { OnboardingPage } from './views/Growth/Onboarding/OnboardingPage'
import { PostConnectPage } from './views/Growth/PostConnect/PostConnectPage'
import { OAuth2AppsManagement } from './views/OAuth2App/OAuth2AppManagement/OAuth2AppsManagement'
import { ManageOAuth2App } from './views/OAuth2App/ManageOAuth2App/ManageOAuth2App'
import { CreateOAuth2AppWizard } from './views/OAuth2App/CreateOAuth2App/CreateOAuth2AppWizard'
import { CreateCustomIntegrationWizard } from './views/CustomIntegration/Create/CreateCustomIntegrationWizard'
import { CustomIntegrationDetailsPage } from './views/CustomIntegration/Details/CustomIntegrationDetailsPage'
import { isCustomIntegrationsFeatureEnabled } from './views/CustomIntegration/constants'
import { CustomIntegrationCallbackView } from './views/CustomIntegration/Callback/CustomIntegrationCallbackView'
import { FeatureAdvertisementRoute } from './components/FeatureAdvertisement/FeatureAdvertisementRoute'
import { useIsOnboardingActive } from './store/onboarding/useIsOnboardingActive'
import { DocumentUploadPage } from './views/ProcessDocument/DocumentUploadPage'
import { DocumentBatchPage } from './views/ProcessDocument/DocumentBatchPage'
import { DocumentPage } from './views/ProcessDocument/DocumentPage'
import { DocumentsPage } from './views/ProcessDocument/DocumentsPage'
import { SignUpPageChangeView } from './views/SignUpChange/SignUpPageChangeView'
import { UserScoresRoutes } from './views/Insights/UserScores/UserScoresRoutes'

const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV7(createBrowserRouter)

const clientProviderConfiguration: ClientProviderConfiguration = {
  mediaApiBaseUrl: import.meta.env.REACT_APP_API_BASE_URL!,
  aiApiBaseUrl: import.meta.env.REACT_APP_AI_BASE_URL!,
  aiExecuteApiBaseUrl: import.meta.env.REACT_APP_AI_EXECUTE_API_BASE_URL!,
  trackingApiBaseUrl: import.meta.env.REACT_APP_TRACKING_API_BASE_URL!,
  trackingEnabled: import.meta.env.REACT_APP_TRACKING_ENABLED === 'true',
}

const AppComponent: React.FC = () => {
  const isLoading = useAppSelector(selectCrossAccountLoginLoading)
  return (
    <>
      <LoadingOverlay show={isLoading} message='Switching accounts…' />
      <AppRoutes />
    </>
  )
}

const ProfiledApp = Sentry.withProfiler(AppComponent)

export const App: React.FC = () => <ProfiledApp />

const RootErrorElement: React.FC = () => {
  const error = useRouteError()

  React.useEffect(() => {
    Sentry.captureException(error)
  }, [error])

  return (
    <div className='d-flex flex-column align-items-center justify-content-center vh-100'>
      <h1 className='mb-3'>Something went wrong</h1>
      <p className='text-center'>An unexpected error has occurred. Please try refreshing the page or contact support if the issue persists.</p>
    </div>
  )
}

const InsightsRouteGuard: React.FC = () => {
  if (!isInsightsEnabled()) {
    return <Navigate to='/' replace />
  }
  return <InsightsView />
}

const AppRoutes = () => {
  const authenticatedUser = useAppSelector(selectAuthenticatedUser)

  const router = sentryCreateBrowserRouter(
    createRoutesFromElements(
      <Route
        path='/'
        element={
          authenticatedUser ? (
            <APIClientProvider config={clientProviderConfiguration} accountId={authenticatedUser.accountId}>
              <Outlet />
            </APIClientProvider>
          ) : null
        }
        errorElement={<RootErrorElement />}
      >
        {/*TODO: UPDATE TO CORRECT PERMISSION */}
        <Route path='/oauth/authorize' element={<AuthorizedRoute allowedTo={[Permission.ROOM_LIST]} checkAuthenticationOnly={true} />}>
          <Route index={true} element={<OAuthAuthorize />} />
        </Route>
        <Route path='/mfa' element={<AuthorizedRoute allowedTo={[Permission.MFA_READ]} checkAuthenticationOnly={true} />}>
          <Route index={true} element={<MFALogin />} />
        </Route>
        <Route path='/sign-up-requested' element={<AuthorizedRoute allowedTo={[Permission.USER_READ]} checkAuthenticationOnly />}>
          <Route index element={<SignUpPageChangeView />} />
        </Route>
        <Route path='/insights/coaching/*' element={<UserScoresRoutes />} />
        <Route
          element={
            <AuthorizedRoute allowedTo={[Permission.ROOM_LIST]} checkAuthenticationOnly={true}>
              <LockScreenWrapper>
                <LayoutWithLeftSidebar />
              </LockScreenWrapper>
            </AuthorizedRoute>
          }
        >
          <Route index={true} element={<HomePageNavigate />} />
        </Route>
        <Route
          path='/onboarding'
          element={
            <AuthorizedRoute allowedTo={[Permission.ROOM_LIST]} checkAuthenticationOnly={true}>
              <LockScreenWrapper>
                <LayoutWithLeftSidebar />
              </LockScreenWrapper>
            </AuthorizedRoute>
          }
        >
          <Route index={true} element={<OnboardingPage />} />
          <Route path='post-connect/:serviceId' element={<PostConnectPage />} />
        </Route>

        <Route path='/getting-started/*' element={<Navigate to='/onboarding' replace />} />

        <Route
          path='/workspace'
          element={
            <AuthorizedRoute allowedTo={[Permission.ROOM_LIST]}>
              <LockScreenWrapper>
                <LayoutWithLeftSidebar />
              </LockScreenWrapper>
            </AuthorizedRoute>
          }
        >
          <Route index={true} element={<LiveMeetings />} />
          <Route
            path='dashboard/'
            element={
              <CustomWorkflowsGuard>
                <WorkflowsLanding />
              </CustomWorkflowsGuard>
            }
          />
          <Route
            path='account-opening'
            element={
              <AccountOpeningGate>
                <AccountOpeningWizard />
              </AccountOpeningGate>
            }
          />
          <Route path='upcoming/'>
            <Route index={true} element={<Upcoming />} />
            <Route path='room/:roomId/session/:sessionId/' element={<FinancialAdvisorSummary />} />
          </Route>
          <Route path='live-meetings/'>
            <Route index={true} element={<LiveMeetings />} />
            <Route path='room/:roomId/session/:sessionId/' element={<FinancialAdvisorSummary />} />
          </Route>
          <Route path='in-person-meeting/'>
            <Route path='cleanup-database' element={<CleanupDatabase />} />
            <Route path='mobile-support' element={<MobileSupport />} />
            <Route path='upcoming' element={<CreateInPersonMeetingLoader />} />
            <Route path='live-meetings' element={<CreateInPersonMeetingLoader />} />
          </Route>
          <Route path='setup/'>
            <Route path=':activeTab/'>
              <Route
                path='scheduling-event-type/:operation/:eventTypeId/:tabType?'
                element={
                  <SchedulingQuotaGate>
                    <EventTypeEditorView />
                  </SchedulingQuotaGate>
                }
              />
              <Route
                path='account-booking-page'
                element={
                  <SchedulingQuotaGate>
                    <AccountBookingPageView />
                  </SchedulingQuotaGate>
                }
              />
              <Route path='meeting-type/:operation/:meetingTemplateId/:tabType' element={<MeetingTemplateView />} />
              <Route path='meeting-type/:operation/:meetingTemplateId/:tabType/:modalType' element={<MeetingTemplateView />} />
              <Route path='meeting-type/create/:tabType' element={<MeetingTemplateView />} />
              <Route path='email-type/:operation/:emailTypeId' element={<EmailTypeView />} />
              <Route path='email-type/create/' element={<EmailTypeView />} />
              <Route path='document-type/:operation/:documentTypeId' element={<DocumentTypeView />} />
              <Route path='document-type/create/' element={<DocumentTypeView />} />
              <Route path='form/create' element={<EditAIFormView />} />
              <Route path='form/details/:formId'>
                <Route path=':activeFormTab?' element={<EditAIFormView />} />
                <Route path='templates/create' element={<FormTemplateCreateView />} />
                <Route path='templates/details/:formTemplateId' element={<FormTemplateUpdateView />} />
              </Route>
              <Route path='template/create' element={<TemplateCreationPage />} />
              <Route path='template/create/generate' element={<GenerateTemplatePage />} />
              <Route path='template/:templateId' element={<TemplateEditor />} />
              <Route path='templates' element={<TemplateSelect />} />
              <Route path=':settingsTab' element={<Setup />} />
              <Route path='' element={<Setup />} />
            </Route>
            <Route path='' element={<Setup />} />
          </Route>
          <Route path='past-meetings/'>
            <Route index={true} element={<PastMeetings />} />
            <Route path='room/:roomId/session/:sessionId/' element={<FinancialAdvisorSummary />} />
            <Route path='import' element={<ImportSessionModal />} />
            <Route path='sample-meeting' element={<DemoMeeting />} />
          </Route>
        </Route>
        <Route
          path='/scheduling'
          element={
            <AuthorizedRoute allowedTo={[Permission.ROOM_LIST]}>
              <LockScreenWrapper>
                <LayoutWithLeftSidebar />
              </LockScreenWrapper>
            </AuthorizedRoute>
          }
        >
          <Route index={true} element={<Navigate to='/scheduling/requests' replace />} />
          <Route
            path='schedule'
            element={
              <SchedulingQuotaGate>
                <ScheduleMeetingFlow />
              </SchedulingQuotaGate>
            }
          />
          <Route
            path='reschedule/:bookingId'
            element={
              <SchedulingQuotaGate>
                <RescheduleMeetingFlow />
              </SchedulingQuotaGate>
            }
          />
          <Route
            path=':activeTab'
            element={
              <SchedulingQuotaGate>
                <SchedulingPage />
              </SchedulingQuotaGate>
            }
          />
        </Route>
        <Route
          path='/account'
          element={
            <AuthorizedRoute allowedTo={[Permission.ROOM_LIST]}>
              <LockScreenWrapper>
                <LayoutWithLeftSidebar />
              </LockScreenWrapper>
            </AuthorizedRoute>
          }
        >
          <Route path='manage-team/'>
            <Route path='' element={<ManageTeam />} />
            <Route path=':activeTab' index={true} element={<ManageTeam />} />
            <Route path='users/:userId' element={<UserView />} />
            <Route
              path='users/:userId/tabs/:activeTab'
              element={
                <AuthorizedRoute allowedTo={[Permission.USER_ADMIN]}>
                  <UserView />
                </AuthorizedRoute>
              }
            />
            <Route path='users/:userId/:modal' element={<ManageTeam />} />
            <Route path='grants/:grantId/:modal' element={<ManageTeam />} />
          </Route>

          <Route path='profile-settings/' element={<ProfileSettings />}>
            <Route path=':activeTab' element={<ProfileSettings />} />
          </Route>
          <Route path='profile-settings/integrations' element={<ProfileSettingsIntegrationRedirect />} />

          <Route path='integration-settings/'>
            <Route path='' element={<IntegrationSettings />} />
            <Route path=':section' element={<IntegrationSettings />} />
            <Route path='integrations/:serviceId/:activeTab?' element={<IntegrationManagement />} />
            <Route
              path='oauth2apps/new'
              element={
                <AuthorizedRoute allowedTo={[Permission.OAUTH2_APP_UPDATE]}>
                  <CreateOAuth2AppWizard />
                </AuthorizedRoute>
              }
            />
            <Route
              path='oauth2apps/:appId/manage/:manageTab?'
              element={
                <AuthorizedRoute allowedTo={[Permission.OAUTH2_APP_UPDATE]}>
                  <ManageOAuth2App />
                </AuthorizedRoute>
              }
            />
            <Route path='oauth2apps/:appId/:activeTab?' element={<OAuth2AppsManagement />} />
            {isCustomIntegrationsFeatureEnabled() ? (
              <>
                <Route
                  path='custom-integrations/new'
                  element={
                    <AuthorizedRoute allowedTo={[Permission.CUSTOM_INTEGRATION_CREATE]}>
                      <CreateCustomIntegrationWizard />
                    </AuthorizedRoute>
                  }
                />
                <Route path='custom-integrations/:customIntegrationId/:tab?' element={<CustomIntegrationDetailsPage />} />
              </>
            ) : null}
          </Route>
        </Route>

        <Route
          path='/account/plan'
          element={
            <AuthorizedRoute allowedTo={[Permission.BILLING_SUBSCRIPTION_CHANGE]}>
              <LockScreenWrapper>
                <LayoutWithLeftSidebar />
              </LockScreenWrapper>
            </AuthorizedRoute>
          }
        >
          <Route index element={<Plan />} />
          <Route path='free-trial/oauth2apps/:appId' element={<DeltaTrial />} />
          <Route path='free-trial/:featureId' element={<DeltaTrial />} />
          <Route path='free-trial/oauth2apps/:appId/success' element={<DeltaTrialSuccess />} />
          <Route path='free-trial/:featureId/success' element={<DeltaTrialSuccess />} />
          <Route path=':activeTab' element={<Plan />} />
        </Route>

        <Route
          path='/library'
          element={
            <AuthorizedRoute allowedTo={[Permission.ROOM_LIST]}>
              <LockScreenWrapper>
                <LayoutWithLeftSidebar />
              </LockScreenWrapper>
            </AuthorizedRoute>
          }
        >
          <Route path='manage-contacts/'>
            <Route path='contacts/:userId' element={<Contact />} />
            <Route path='contacts/:userId/tabs/:activeTab/threads/:threadId' element={<Contact />}>
              <Route path='' element={<Contact />} />
              <Route path='modal/:modal/connectors/:serviceId' element={<Contact />} />
              <Route path='modal/:modal/contacts/:contactId' element={<Contact />} />
              <Route path='modal/:modal/contacts/:contactId/connectors/:serviceId' element={<Contact />} />
            </Route>
            <Route path='contacts/:userId/tabs/:activeTab/:scorecardTab?' element={<Contact />} />

            <Route element={<AuthorizedRoute allowedTo={[Permission.DOCUMENT_LIST]} />}>
              <Route element={<FeatureAdvertisementRoute bypassQuotas={['ai.documentSummaryAgent.value']} />}>
                <Route path='contacts/:userId/documents'>
                  <Route path='upload' element={<DocumentUploadPage />} />
                </Route>

                <Route path='contacts/documents'>
                  <Route path='process'>
                    <Route path='' element={<DocumentProspectingPage />} />
                  </Route>
                </Route>
              </Route>
            </Route>
            <Route path='' element={<ManageContacts />}>
              <Route path='contacts/:userId/:modal' element={<ManageContacts />} />
            </Route>
          </Route>
        </Route>

        <Route
          path='/cross-client'
          element={
            <AuthorizedRoute allowedTo={[Permission.ROOM_LIST]}>
              <LockScreenWrapper>
                <LayoutWithLeftSidebar />
              </LockScreenWrapper>
            </AuthorizedRoute>
          }
        >
          <Route index element={<CrossClient />} />
          <Route path='executions/:executionId' element={<CrossClientResults />} />
          <Route path='executions/:executionId/schedule' element={<ScheduleQueryView />} />
          <Route path='executions/:executionId/schedule-meeting' element={<SendBookingInvitationsView />} />
          <Route path='scheduled-queries/:scheduledQueryId/edit' element={<EditScheduledQueryView />} />
        </Route>

        <Route
          path='/analytics-hub'
          element={
            <AuthorizedRoute allowedTo={[Permission.DASHBOARD_LIST]}>
              <LockScreenWrapper>
                <LayoutWithLeftSidebar containerClassName='flex-grow-1 d-flex' />
              </LockScreenWrapper>
            </AuthorizedRoute>
          }
        >
          <Route element={<FeatureAdvertisementRoute bypassQuotas={['insights.enabled.value']} />}>
            <Route index element={<Analytics />} />
          </Route>
        </Route>

        <Route
          path='/insights'
          element={
            <AuthorizedRoute allowedTo={[Permission.INSIGHTS_READ]}>
              <LockScreenWrapper>
                <LayoutWithLeftSidebar containerClassName='flex-grow-1 d-flex' />
              </LockScreenWrapper>
            </AuthorizedRoute>
          }
        >
          <Route element={<FeatureAdvertisementRoute bypassQuotas={['insights.enabled.value']} />}>
            <Route index element={<InsightsRouteGuard />} />
          </Route>
        </Route>

        <Route
          path='/email'
          element={
            <AuthorizedRoute allowedTo={[Permission.INTEGRATION_MANAGE_EMAIL]}>
              <LockScreenWrapper>
                <LayoutWithLeftSidebar />
              </LockScreenWrapper>
            </AuthorizedRoute>
          }
        >
          <Route path='inbox/' element={<FeatureAdvertisementRoute bypassQuotas={['integration.emailEnabled.value']} />}>
            <Route path='threads/:threadId' element={<EmailHubV2 />}>
              <Route path='' element={<EmailHubV2 />} />
              <Route path='modal/:modal/connectors/:serviceId' element={<EmailHubV2 />} />
              <Route path='modal/:modal/contacts/:contactId' element={<EmailHubV2 />} />
              <Route path='modal/:modal/contacts/:contactId/connectors/:serviceId' element={<EmailHubV2 />} />
            </Route>
            <Route path='' element={<EmailHubV2 />} />
          </Route>
          <Route path='new' element={<FeatureAdvertisementRoute bypassQuotas={['integration.emailEnabled.value']} />}>
            <Route path='' element={<NewEmailPage />} />
          </Route>
        </Route>

        <Route
          path='/documents'
          element={
            <AuthorizedRoute allowedTo={[Permission.DOCUMENT_LIST]}>
              <LockScreenWrapper>
                <LayoutWithLeftSidebar />
              </LockScreenWrapper>
            </AuthorizedRoute>
          }
        >
          <Route path='' element={<FeatureAdvertisementRoute bypassQuotas={['ai.documentSummaryAgent.value']} />}>
            <Route path='' element={<DocumentsPage />} />
            <Route path='upload' element={<DocumentUploadPage />} />
            <Route path=':documentId' element={<DocumentPage />} />
          </Route>
        </Route>

        <Route
          path='/document-batches'
          element={
            <AuthorizedRoute allowedTo={[Permission.DOCUMENT_LIST]}>
              <LockScreenWrapper>
                <LayoutWithLeftSidebar />
              </LockScreenWrapper>
            </AuthorizedRoute>
          }
        >
          <Route path=':documentBatchId' element={<FeatureAdvertisementRoute bypassQuotas={['ai.documentSummaryAgent.value']} />}>
            <Route path='' element={<DocumentBatchPage />} />
            <Route path='documents/:documentId' element={<DocumentPage />} />
          </Route>
        </Route>

        <Route
          path='/integrations'
          element={
            <div>
              <Outlet />
            </div>
          }
        >
          <Route
            path='callback/custom/:customIntegrationId'
            element={
              <AuthorizedRoute allowedTo={[Permission.ROOM_LIST]} checkAuthenticationOnly={true}>
                <CustomIntegrationCallbackView />
              </AuthorizedRoute>
            }
          />
          <Route
            path={'callback/:integrationType'}
            element={
              <AuthorizedRoute allowedTo={[Permission.ROOM_LIST]} checkAuthenticationOnly={true}>
                <IntegrationCallbackView />
              </AuthorizedRoute>
            }
          />
        </Route>
        <Route
          path='/logout'
          element={
            <AuthorizedRoute allowedTo={[Permission.ROOM_LIST]} checkAuthenticationOnly={true}>
              <LayoutWithLeftSidebar>
                <Logout />
              </LayoutWithLeftSidebar>
            </AuthorizedRoute>
          }
        />
      </Route>,
    ),
    { basename: ROUTER_BASEPATH },
  )
  return <RouterProvider router={router} />
}

const HomePageNavigate: React.FC = () => {
  const { isActive, isLoading } = useIsOnboardingActive()
  const { appendUTMParams } = useUTMHandler()
  if (isLoading) {
    return null
  }
  return <Navigate to={isActive ? appendUTMParams('/onboarding', true) : appendUTMParams('/workspace/live-meetings', true)} replace />
}
